(ns ui.components.date-range-picker.view
  (:require
   [common.localization :as loc]
   [common.util :as cu]
   ["date-fns" :as date-fns]
   ["date-fns/locale" :as date-fns-locale]
   ["react-date-range" :refer (DateRangePicker)]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.components.date-picker.view :as date-picker]
   [ui.util :as uu]))

(def default-min-date
  (js/Date. 1000 1 1))

(def default-max-date
  (js/Date. 3000 11 31 23 59))

(defn utc-midnight-ts
  [local-date]
  (when local-date
    (let [year (.getFullYear local-date)
          month (.getMonth local-date)
          day (.getDate local-date)]
      (js/Date.UTC year month day 0 0 0 0))))

(defn utc-end-of-day-ts
  [local-date]
  (when local-date
    (let [year (.getFullYear local-date)
          month (.getMonth local-date)
          day (.getDate local-date)]
      (js/Date.UTC year month day 23 59 59 0))))

(defn utc-ts->local-date
  [value]
  (when value
    (let [date-value (js/Date. value)]
      (js/Date. (.getUTCFullYear date-value)
                (.getUTCMonth date-value)
                (.getUTCDate date-value)))))

(defn format-date
  [date-value date-format]
  (when date-value
    (date-fns/format date-value date-format #js {:locale date-fns-locale/enUs})))

(defn parse-input-date
  [value date-format]
  (let [trimmed (cu/str-trim value)]
    (when-not (cu/blank-value? trimmed)
      (let [parsed (date-fns/parse trimmed date-format (js/Date.) #js {:locale date-fns-locale/enUs})]
        (when (date-fns/isValid parsed)
          parsed)))))

(defn- text-input
  [{:keys [value placeholder on-change]}]
  [:input
   {:class       (uu/cm "h-12 w-full rounded border border-solid border-input-light-border-paler"
                        "px-3 text-lg text-input-light-text outline-none"
                        "placeholder:text-input-light-text-paler"
                        "hover:border-input-light-border focus:border-input-light-border")
    :value       (or value "")
    :placeholder placeholder
    :on-change   (fn [e]
                   (on-change (.. e -target -value)))}])

(defn view
  [{:keys [title description min-date max-date date-format apply-label clear-label week-starts-on]
    :or   {title "Select custom date range"
           description "Select a start and end date for your custom range."
           apply-label "Apply filter"
           clear-label "Clear selection"}}]
  (let [min-date (or min-date default-min-date)
        max-date (or max-date default-max-date)
        date-format (or date-format (loc/tr :date-format))
        week-starts-on (or week-starts-on (date-picker/locale-week-starts-on))
        local-state (r/atom {:startDate nil
                             :endDate nil
                             :key "selection"
                             :start-input ""
                             :end-input ""
                             :synced-value [nil nil]
                             :dirty? false
                             :input-error? false})]
    (fn [{:keys [visible? value on-change on-close on-apply]}]
      (when (not= false visible?)
        (let [incoming-start (:start-ts value)
              incoming-end (:end-ts value)
              incoming-value [incoming-start incoming-end]
              incoming-start-date (utc-ts->local-date incoming-start)
              incoming-end-date (utc-ts->local-date incoming-end)
              {:keys [synced-value]} @local-state
              _ (when (not= incoming-value synced-value)
                  (swap! local-state assoc
                         :startDate incoming-start-date
                         :endDate incoming-end-date
                         :start-input (format-date incoming-start-date date-format)
                         :end-input (format-date incoming-end-date date-format)
                         :synced-value incoming-value
                         :dirty? false
                         :input-error? false))
              {:keys [startDate endDate start-input end-input dirty? input-error?]} @local-state
              picker-start-date (or startDate endDate (js/Date.))
              picker-end-date (or endDate startDate picker-start-date)
              start-ts (utc-midnight-ts startDate)
              end-ts (utc-end-of-day-ts endDate)
              has-range? (and start-ts end-ts)
              invalid-order? (and has-range? (> start-ts end-ts))
              apply-disabled? (or (not dirty?)
                                  (not has-range?)
                                  invalid-order?
                                  input-error?)
              handle-calendar-change (fn [item]
                                       (let [selection (or (aget item "selection")
                                                           (.-selection item))
                                             start-date (or (some-> selection (aget "startDate"))
                                                            (some-> selection (.-startDate)))
                                             end-date (or (some-> selection (aget "endDate"))
                                                          (some-> selection (.-endDate)))
                                             resolved-end-date (or end-date start-date)]
                                         (when (or start-date end-date)
                                           (swap! local-state assoc
                                                  :startDate start-date
                                                  :endDate resolved-end-date
                                                  :start-input (format-date start-date date-format)
                                                  :end-input (format-date resolved-end-date date-format)
                                                  :dirty? true
                                                  :input-error? false))))
              update-start-input (fn [input-value]
                                   (let [parsed (parse-input-date input-value date-format)]
                                     (swap! local-state assoc
                                            :start-input input-value
                                            :startDate parsed
                                            :dirty? true
                                            :input-error? (and (not (cu/blank-value? input-value))
                                                               (nil? parsed)))))
              update-end-input (fn [input-value]
                                 (let [parsed (parse-input-date input-value date-format)]
                                   (swap! local-state assoc
                                          :end-input input-value
                                          :endDate parsed
                                          :dirty? true
                                          :input-error? (and (not (cu/blank-value? input-value))
                                                             (nil? parsed)))))
              clear-selection (fn []
                                (swap! local-state assoc
                                       :startDate nil
                                       :endDate nil
                                       :start-input ""
                                       :end-input ""
                                       :dirty? true
                                       :input-error? false)
                                (when (fn? on-change)
                                  (on-change {:start-ts nil
                                              :end-ts nil
                                              :range-kw :custom})))
              apply-selection (fn []
                                (let [payload {:start-ts start-ts
                                               :end-ts end-ts
                                               :range-kw :custom}]
                                  (swap! local-state assoc
                                         :synced-value [start-ts end-ts]
                                         :dirty? false)
                                  (when (fn? on-change)
                                    (on-change payload))
                                  (when (fn? on-apply)
                                    (on-apply payload))))]
          [:div
           {:class "fixed inset-0 z-[1055] bg-black/50 p-6"}
           [:div
            {:class "mx-auto w-full max-w-[1600px] rounded-xl border border-solid border-base-light-border-paler bg-base-light p-8 shadow"}
            [:div {:class "flex items-start justify-between"}
             [:div
              [:h2 {:class "m-0 text-4xl font-semibold text-base-regular-text"} title]
              [:p {:class "mt-2 mb-0 text-xl text-base-light-text-paler"} description]]
             ;; Closes the overlay. It looks identical to the date picker's
             ;; clear button and does something else entirely.
             [button/square-secondary-open
              {:tooltip    (loc/tr :close)
               :aria-label (loc/tr :close)
               :on-click   on-close}
              [:i {:class "fal fa-times text-2xl"}]]]

            [:div {:class "mt-8"}
             [:> DateRangePicker
              {:months                         2
               :rangeColors                    ["#8ABF4C"]
               :color                          "#8ABF4C"
               :direction                      "horizontal"
               :showDateDisplay                false
               :showSelectionPreview           false
               :showMonthAndYearPickers        true
               :editableDateInputs             false
               :moveRangeOnFirstSelection      false
               :retainEndDateOnFirstSelection  false
               :minDate                        min-date
               :maxDate                        max-date
               :ranges                         [(clj->js {:startDate picker-start-date
                                                          :endDate picker-end-date
                                                          :key "selection"})]
               :staticRanges                   #js []
               :inputRanges                    #js []
               :weekStartsOn                   week-starts-on
               :onChange                       handle-calendar-change}]]

            [:div {:class "mt-5"}
             [:div {:class "mb-2 text-3xl text-base-regular-text"} "Date Range"]
             [:div {:class "flex items-center gap-3"}
              [text-input
               {:value start-input
                :placeholder (or date-format "DD/MM/YYYY")
                :on-change update-start-input}]
              [:div {:class "px-1 text-4xl text-base-light-text-paler"} "to"]
              [text-input
               {:value end-input
                :placeholder (or date-format "DD/MM/YYYY")
                :on-change update-end-input}]]]

            [:div {:class "mt-8 flex items-center justify-between"}
             [button/secondary-solid
              {:on-click clear-selection
               :class "px-6 py-3 text-2xl"
               :disabled? (and (cu/blank-value? start-input)
                               (cu/blank-value? end-input))}
              clear-label]
             [button/primary-solid
              {:on-click apply-selection
               :class "px-6 py-3 text-2xl"
               :disabled? apply-disabled?}
              apply-label]]]])))))
